import { ArrowRight, Check } from "lucide-react";
import { colors, type, spacing, radius, formatCents } from "@/theme";

export const NEXT = {
  in_attesa_conferma: "confermato",
  confermato: "in_preparazione",
  in_preparazione: "pronto",
  pronto: "servito",
};
const STATE_LABEL = {
  in_attesa_conferma: "Da confermare",
  confermato: "Confermato",
  in_preparazione: "In preparazione",
  pronto: "Pronto",
  servito: "Servito",
  annullato: "Annullato",
};
const STATE_COLOR = {
  in_attesa_conferma: colors.warning,
  confermato: colors.ink,
  in_preparazione: colors.accent,
  pronto: colors.success,
  servito: colors.inkSoft,
  annullato: colors.danger,
};
const ADVANCE_LABEL = {
  in_attesa_conferma: "Conferma",
  confermato: "Inizia preparazione",
  in_preparazione: "Segna pronto",
  pronto: "Segna servito",
};

export function minsSince(iso) { return Math.floor((Date.now() - new Date(iso).getTime()) / 60000); }
function secsSince(iso) { return Math.floor((Date.now() - new Date(iso).getTime()) / 1000); }
function hhmm(iso) { const d = new Date(iso); return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`; }

export default function KdsTableCard({ t, readOnly, highlightOrderId, onAdvance, confirmPay, setConfirmPay, onPay, pulse }) {
  const pending = t.orders.filter((o) => ["confermato", "in_preparazione", "in_attesa_conferma"].includes(o.stato));
  const oldest = pending.length ? Math.max(...pending.map((o) => minsSince(o.created_at))) : 0;
  const idle = !readOnly && oldest >= 3;
  return (
    <div data-testid={`kds-table-${t.etichetta}`} style={{ background: colors.surface, borderRadius: radius.lg, padding: spacing.md, border: `${idle ? 2 : 1}px solid ${idle ? colors.danger : colors.line}`, display: "flex", flexDirection: "column", gap: spacing.sm, opacity: idle && pulse ? 0.4 + pulse * 0.6 : 1 }}>
      <div style={{ display: "flex", alignItems: "center", gap: spacing.sm }}>
        <div style={{ width: 48, height: 48, borderRadius: radius.md, background: colors.ink, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <span style={{ ...type.sansBold, fontSize: 20, color: colors.bg }}>{t.etichetta}</span>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ ...type.sansBold, fontSize: 20, color: colors.ink }}>Tavolo {t.etichetta}</div>
          <div style={{ ...type.sans, fontSize: 13, color: colors.inkSoft }}>{t.commensali.length} pers · aperto da {minsSince(t.aperta_at)} min</div>
        </div>
        {idle ? <div style={{ background: colors.danger, borderRadius: radius.pill, padding: `4px ${spacing.sm}px` }}><span style={{ color: "#fff", ...type.sansSemi, fontSize: 13 }}>{oldest}′</span></div> : null}
      </div>

      {t.orders.map((o) => {
        const isNew = highlightOrderId ? o.id === highlightOrderId : secsSince(o.created_at) < 60;
        const done = o.stato === "servito" || o.stato === "annullato";
        return (
          <div key={o.id} data-testid={`kds-order-${o.id}`} style={{ background: isNew ? colors.accentSoft : colors.bg, borderRadius: radius.md, padding: spacing.md, border: `${isNew ? 1.5 : 1}px solid ${isNew ? colors.accent : colors.line}`, display: "flex", flexDirection: "column", gap: spacing.xs, opacity: done ? 0.5 : 1 }}>
            {readOnly && isNew ? <div data-testid="kds-just-arrived" style={{ ...type.sansBold, fontSize: 11, letterSpacing: 1.5, color: colors.accent }}>APPENA ARRIVATO</div> : null}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: spacing.xs }}>
              <span style={{ ...type.sansSemi, fontSize: 15, color: colors.ink }}>{o.guest_nome} · #{o.numero}{readOnly ? ` · ${hhmm(o.created_at)}` : ""}</span>
              <span style={{ borderRadius: radius.sm, padding: `3px ${spacing.sm}px`, background: STATE_COLOR[o.stato], color: "#fff", ...type.sansSemi, fontSize: 11 }}>{STATE_LABEL[o.stato]}</span>
            </div>
            {o.righe.map((r, i) => (
              <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: spacing.sm, padding: "3px 0" }}>
                <span style={{ ...type.sansBold, fontSize: 18, color: colors.ink, minWidth: 30 }}>{r.quantita}×</span>
                <div style={{ flex: 1 }}>
                  <div style={{ ...type.sansMedium, fontSize: 17, color: colors.ink }}>{r.nome_snapshot}</div>
                  {(r.note_rapide?.length || r.nota_riga) ? <div style={{ ...type.sans, fontSize: 14, color: colors.accent, marginTop: 1 }}>{[...(r.note_rapide || []), r.nota_riga].filter(Boolean).join(" · ")}</div> : null}
                </div>
              </div>
            ))}
            {!readOnly && NEXT[o.stato] ? (
              <button data-testid={`advance-${o.id}`} onClick={() => onAdvance?.(o.id, o.stato)} style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: spacing.sm, background: colors.accent, height: 44, border: "none", borderRadius: radius.md, marginTop: spacing.xs, color: colors.onAccent, ...type.sansSemi, fontSize: 15 }}>
                <ArrowRight size={16} color={colors.onAccent} />{ADVANCE_LABEL[o.stato]}
              </button>
            ) : null}
          </div>
        );
      })}

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: spacing.xs, paddingTop: spacing.sm, borderTop: `1px solid ${colors.line}` }}>
        <span style={{ ...type.sansSemi, fontSize: 14, color: colors.ink }}>Conto tavolo: {formatCents(t.totale_cent)}</span>
        {readOnly ? null : confirmPay === t.session_id ? (
          <div style={{ display: "flex", gap: spacing.sm }}>
            <button data-testid={`pay-cancel-${t.session_id}`} onClick={() => setConfirmPay?.(null)} style={{ padding: `${spacing.sm}px ${spacing.md}px`, borderRadius: radius.md, border: `1px solid ${colors.line}`, background: "transparent", ...type.sansSemi, fontSize: 14, color: colors.inkSoft }}>No</button>
            <button data-testid={`pay-confirm-${t.session_id}`} onClick={() => onPay?.(t.session_id)} style={{ padding: `${spacing.sm}px ${spacing.md}px`, borderRadius: radius.md, border: "none", background: colors.success, color: "#fff", ...type.sansSemi, fontSize: 14 }}>Sì, chiudi</button>
          </div>
        ) : (
          <button data-testid={`pay-${t.session_id}`} onClick={() => setConfirmPay?.(t.session_id)} style={{ display: "flex", alignItems: "center", gap: spacing.xs, background: colors.successSoft, padding: `${spacing.sm}px ${spacing.md}px`, border: "none", borderRadius: radius.md, ...type.sansSemi, fontSize: 14, color: colors.success }}>
            <Check size={16} color={colors.success} />Segna pagato
          </button>
        )}
      </div>
    </div>
  );
}
